import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ServiceOfferingShopCategory } from '@freelancer/datastore/collections';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HeadingColor, HeadingType } from '@freelancer/ui/heading';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import {
  InputEditInlineType,
  InputSize,
  InputType,
} from '@freelancer/ui/input';
import { LinkIconPosition, LinkUnderline } from '@freelancer/ui/link';
import { FontColor, FontType, FontWeight, TextSize } from '@freelancer/ui/text';
import { dirtyAndValidate } from '@freelancer/ui/validators';
import type { FormGroupTypeOf } from '@freelancer/utils';
import type {
  CarouselHeaderFormType,
  CategorySaveEvent,
} from './service-offering-carousel.types';

@Component({
  selector: 'fl-service-offering-carousel-header-display',
  template: `
    <!--
      DESKTOP:
        - category name and description are side by side
        - category name and description are both part of the link
        - no chevron icon at the end of the link
    -->
    <fl-link
      flTrackingLabel="ServiceOfferingCategoryLink"
      i18n="Service offering carousel category name and description"
      [flHideMobile]="true"
      [link]="category.seoUrl"
      [underline]="LinkUnderline.NEVER"
    >
      <div class="CategoryCarouselHeader">
        <fl-heading
          [color]="HeadingColor.PRIMARY"
          [size]="TextSize.LARGE"
          [sizeDesktop]="TextSize.XLARGE"
          [headingType]="HeadingType.H2"
          [flMarginRight]="'xxsmall'"
        >
          {{ category.name }}.
        </fl-heading>
        <span class="text-foreground text-small desktop:text-xlarge">
          {{ category.description }}
        </span>
      </div>
    </fl-link>
    <!--
      MOBILE:
        - category name and description are stacked vertically
        - only the category name is part of the link
        - has chevron icon at the end of the link
    -->
    <div [flShowMobile]="true">
      <fl-link
        flTrackingLabel="ServiceOfferingCategoryLink"
        i18n="Service offering carousel category name"
        [iconAnimation]="true"
        [iconColor]="IconColor.PRIMARY"
        [iconLabel]="'View ' + category.name + ' services'"
        [iconName]="'ui-chevron-right'"
        [iconPosition]="LinkIconPosition.RIGHT"
        [iconSize]="IconSize.SMALL"
        [link]="category.seoUrl"
        [underline]="LinkUnderline.NEVER"
      >
        <fl-heading
          [color]="HeadingColor.PRIMARY"
          [size]="TextSize.LARGE"
          [sizeDesktop]="TextSize.XLARGE"
          [headingType]="HeadingType.H2"
          [flMarginRight]="'xxsmall'"
        >
          {{ category.name }}
        </fl-heading>
      </fl-link>
      <p class="text-foreground text-small desktop:text-xlarge">
        {{ category.description }}
      </p>
    </div>
  `,
  styleUrls: ['./service-offering-carousel-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceOfferingCarouselHeaderDisplayComponent {
  FontColor = FontColor;
  FontType = FontType;
  HeadingColor = HeadingColor;
  HeadingType = HeadingType;
  IconColor = IconColor;
  IconSize = IconSize;
  LinkIconPosition = LinkIconPosition;
  LinkUnderline = LinkUnderline;
  TextSize = TextSize;

  @Input() category: ServiceOfferingShopCategory;
}

@Component({
  selector: 'fl-service-offering-carousel-header-edit',
  template: `
    <div
      class="CategoryCarouselHeader"
      [flHideMobile]="false"
      [flHideTablet]="false"
      [flHideDesktop]="true"
      [flMarginBottom]="'xxsmall'"
      [flMarginRight]="'small'"
      [flMarginRightDesktop]="'none'"
    >
      <fl-input
        [control]="form.controls.name"
        [editInline]="InputEditInlineType.BORDERED_BOTTOM"
        [type]="InputType.TEXT"
        [fontWeight]="FontWeight.BOLD"
        [placeholder]="namePlaceholder"
        [size]="InputSize.MID"
        [editInlineTitleSize]="'large'"
      >
      </fl-input>
      <fl-input
        [control]="form.controls.description"
        [editInline]="InputEditInlineType.BORDERED_BOTTOM"
        [type]="InputType.TEXT"
        [placeholder]="descriptionPlaceholder"
        [size]="InputSize.SMALL"
        [flMarginBottom]="'xsmall'"
      >
      </fl-input>
      <div class="ActionContainer">
        <fl-button
          i18n="Cancel button"
          flTrackingLabel="CancelButton"
          class="ActionButton"
          [color]="ButtonColor.TEXT_FOREGROUND"
          [size]="ButtonSize.SMALL"
          (click)="handleCancel()"
        >
          Cancel
        </fl-button>
        <fl-button
          i18n="Save button"
          flTrackingLabel="SaveButton"
          class="ActionButton"
          [color]="ButtonColor.PRIMARY"
          [size]="ButtonSize.SMALL"
          (click)="handleSave()"
        >
          Save
        </fl-button>
      </div>
    </div>
    <div
      class="CategoryCarouselHeader"
      [flHideMobile]="true"
      [flHideTablet]="true"
      [flHideDesktop]="false"
      [flMarginBottom]="'small'"
    >
      <fl-input
        class="CategoryCarouselHeader-desktopFormName"
        [control]="form.controls.name"
        [editInline]="InputEditInlineType.BORDERED_BOTTOM"
        [type]="InputType.TEXT"
        [fontWeight]="FontWeight.BOLD"
        [placeholder]="namePlaceholder"
        [size]="InputSize.LARGE"
        [editInlineTitleSize]="'xxlarge'"
        [flMarginRight]="'xxsmall'"
      >
      </fl-input>
      <fl-input
        class="CategoryCarouselHeader-desktopFormDescription"
        [control]="form.controls.description"
        [editInline]="InputEditInlineType.BORDERED_BOTTOM"
        [type]="InputType.TEXT"
        [placeholder]="descriptionPlaceholder"
        [size]="InputSize.LARGE"
        [editInlineTitleSize]="'xxlarge'"
        [flMarginRightDesktop]="'xsmall'"
      >
      </fl-input>
      <div class="ActionContainer">
        <fl-button
          i18n="Cancel button"
          flTrackingLabel="CancelButton"
          class="ActionButton"
          [color]="ButtonColor.TEXT_FOREGROUND"
          [size]="ButtonSize.SMALL"
          (click)="handleCancel()"
        >
          <p
            class="text-color-inherit text-mid ActionButton CancelText underline"
          >
            Cancel
          </p>
        </fl-button>
        <fl-button
          i18n="Save button"
          flTrackingLabel="SaveButton"
          class="ActionButton"
          [color]="ButtonColor.PRIMARY"
          [sizeDesktop]="ButtonSize.LARGE"
          (click)="handleSave()"
        >
          Save
        </fl-button>
      </div>
    </div>
  `,
  styleUrls: ['./service-offering-carousel-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceOfferingCarouselHeaderEditComponent {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  FontColor = FontColor;
  FontWeight = FontWeight;
  InputEditInlineType = InputEditInlineType;
  InputSize = InputSize;
  InputType = InputType;
  TextSize = TextSize;

  @Input() category?: ServiceOfferingShopCategory;
  @Input() form: FormGroup<FormGroupTypeOf<CarouselHeaderFormType>>;
  @Input() namePlaceholder: string;
  @Input() descriptionPlaceholder: string;

  @Output() save = new EventEmitter<CategorySaveEvent>();
  @Output() cancel = new EventEmitter<
    ServiceOfferingShopCategory | undefined
  >();

  handleCancel(): void {
    this.form.setValue({
      name: this.category?.name ?? '',
      description: this.category?.description ?? '',
    });
    this.cancel.emit(this.category);
  }

  handleSave(): void {
    dirtyAndValidate(this.form);
    if (this.form.valid) {
      this.save.emit({
        category: this.category,
        updates: {
          name: this.form.controls.name.value,
          description: this.form.controls.description.value,
        },
      });
    }
  }
}

@Component({
  selector: 'fl-service-offering-carousel-header',
  template: `
    <ng-container *ngIf="isLoading">
      <fl-loading-text
        class="LoadingText"
        [padded]="false"
        [rows]="1"
      ></fl-loading-text>
    </ng-container>

    <ng-container *ngIf="!isEditMode && category && !isLoading">
      <fl-service-offering-carousel-header-display [category]="category">
      </fl-service-offering-carousel-header-display>
    </ng-container>
    <ng-container *ngIf="isEditMode && form && !isLoading">
      <fl-service-offering-carousel-header-edit
        [category]="category"
        [form]="form"
        [namePlaceholder]="namePlaceholder"
        [descriptionPlaceholder]="descriptionPlaceholder"
        (save)="save.emit($event)"
        (cancel)="cancel.emit($event)"
      >
      </fl-service-offering-carousel-header-edit>
    </ng-container>
  `,
  styleUrls: ['./service-offering-carousel-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceOfferingCarouselHeaderComponent {
  @Input() isEditMode = false;
  @Input() isLoading = false;
  @Input() category?: ServiceOfferingShopCategory;
  @Input() form?: FormGroup<FormGroupTypeOf<CarouselHeaderFormType>>;
  @Input() namePlaceholder: string;
  @Input() descriptionPlaceholder: string;

  @Output() cancel = new EventEmitter<
    ServiceOfferingShopCategory | undefined
  >();
  @Output() save = new EventEmitter<CategorySaveEvent>();
}
